import { ChevronDown, ChevronRight, Globe, Laptop, Network, Plus, RefreshCw, Server, Unplug } from '@orgus/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { OS_LABEL } from '@orgus/protocol';
import type { HubAddress, Machine } from '@orgus/protocol';
import { Badge, Button, ErrorState, RelativeTime, Skeleton, StatusDot, formatBytes, machineLabel, machineTone, useNow } from '@orgus/ui';
import { AddMachineDialog } from '../../machines/AddMachineDialog.tsx';
import { ReadinessFound } from '../../machines/Readiness.tsx';
import { useMachines } from '../../../lib/store.ts';
import { loadReach, useOnboarding } from '../client.ts';
import { thisMachine } from '../model.ts';
import { CeilingHint } from '../../trust/CeilingHint.tsx';
import { LANDING_ALLOW_COMMAND } from '../../trust/ceilingHint.ts';
import { Callout } from '../parts.tsx';

const KIND_LABEL: Record<HubAddress['kind'], string> = { loopback: 'This computer', lan: 'Local network', tailscale: 'Tailscale', proxy: 'https proxy' };

function MachineRow({ m, own }: { m: Machine; own: boolean }) {
  const now = useNow(1000);
  const tone = machineTone(m, now);
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-raised text-fg-subtle ring-1 ring-border" aria-hidden>
        {own ? <Laptop className="size-4" /> : <Server className="size-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-ui font-medium text-fg">{m.name}</span>
          {own && <Badge>This machine</Badge>}
        </span>
        <span className="block truncate text-caption text-fg-subtle">
          {OS_LABEL[m.os]} · {m.cpus} CPUs · {formatBytes(m.memTotal, 0)} · agent {m.agentVersion}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="flex items-center gap-2 text-caption text-fg">
          <StatusDot tone={tone} pulse={tone === 'passed'} label="" />
          {tone === 'passed' ? 'Connected, live' : machineLabel(m, now)}
        </span>
        <span className="text-caption text-fg-subtle num">
          <RelativeTime ts={m.lastSeen} prefix="reported" fallback="" />
        </span>
      </span>
    </li>
  );
}

function Reachability() {
  const phase = useOnboarding((s) => s.reachPhase);
  const reach = useOnboarding((s) => s.reach);
  const error = useOnboarding((s) => s.reachError);
  const [busy, setBusy] = useState(false);
  const again = async () => {
    setBusy(true);
    await loadReach(true);
    setBusy(false);
  };
  if (phase === 'loading' && !reach) {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Checking which addresses reach the hub">
        <Skeleton className="h-12" />
        <Skeleton className="h-20" />
      </div>
    );
  }
  if (phase === 'error' || !reach) return <ErrorState error={error} onRetry={again} />;
  const outside = reach.addresses.filter((a) => a.kind !== 'loopback');
  return (
    <div className="flex flex-col gap-3">
      {reach.otherMachines ? (
        <Callout tone="ok" title="The hub listens on your network" action={<Button size="sm" variant="ghost" icon={<RefreshCw />} loading={busy} onClick={again}>Check again</Button>}>
          A machine you add connects out to one of the addresses below. Prefer Tailscale or a private network: the hub speaks plain http. This check runs on this computer, so it
          cannot see a firewall: if a join command times out, allow inbound TCP port {reach.port} on this machine.
        </Callout>
      ) : (
        <Callout title="Machines on other computers are off" action={<Button size="sm" variant="ghost" icon={<RefreshCw />} loading={busy} onClick={again}>Check again</Button>}>
          To add one, use Add machine on that computer, or turn on Settings, Trust, Machines on other computers, only behind HTTPS or a private network such as Tailscale.
        </Callout>
      )}
      {outside.length > 0 && (
        <ul className="divide-y divide-border-subtle rounded-control border border-border-subtle" aria-label="Addresses this machine has">
          {outside.map((a) => (
            <li key={a.url} className="flex items-center gap-3 px-3 py-2 text-ui">
              {a.kind === 'tailscale' ? <Network className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : <Globe className="size-4 shrink-0 text-fg-subtle" aria-hidden />}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-caption text-fg">{a.url}</span>
                <span className="block text-caption text-fg-subtle">
                  {KIND_LABEL[a.kind]}
                  {a.iface ? ` · ${a.iface}` : ''}
                </span>
              </span>
              {a.reachable ? (
                <Badge tone="passed" dot>
                  Listening
                </Badge>
              ) : (
                <Badge tone="offline" icon={<Unplug />}>
                  Not listening
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Step 2: this machine reports live; other machines join by download and approval (WP-03). */
export function MachineStep() {
  const navigate = useNavigate();
  const machines = useMachines();
  const reach = useOnboarding((s) => s.reach);
  const [adding, setAdding] = useState(false);
  const [dev, setDev] = useState(false);
  const own = thisMachine(machines, reach?.hostname);
  const others = machines.filter((m) => m.id !== own?.id);
  // What was found: on this computer, else on the machine that connected last (R-ON-4).
  const found = own ?? others.filter((m) => m.online).sort((a, b) => (b.connectedAt ?? 0) - (a.connectedAt ?? 0))[0];

  return (
    <div className="flex flex-col gap-4">
      {machines.length === 0 ? (
        <div className="flex items-center gap-3 rounded-control border border-border-subtle bg-surface px-4 py-3" role="status" aria-live="polite">
          <StatusDot tone="working" label="" />
          <div className="min-w-0 flex-1 text-ui">
            <p className="text-fg">Waiting for this computer to connect</p>
            <p className="text-caption text-fg-subtle">It reports as soon as its agent starts.</p>
          </div>
          <Button size="sm" icon={<Plus />} onClick={() => setAdding(true)}>
            Add machine
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-border-subtle rounded-control border border-border bg-surface" aria-label="Connected machines">
          {own && <MachineRow m={own} own />}
          {others.map((m) => (
            <MachineRow key={m.id} m={m} own={false} />
          ))}
        </ul>
      )}

      {found && <ReadinessFound machine={found} where={found === own ? 'this computer' : found.name} onAdded={() => navigate('/getting-started?step=repo')} />}

      {machines.length > 0 && <CeilingHint lead="To verify landings and run declared actions on this machine, allow them once there:" command={LANDING_ALLOW_COMMAND} />}

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <h4 className="flex-1 text-ui font-medium text-fg">Add another machine</h4>
          <Button size="sm" icon={<Plus />} onClick={() => setAdding(true)}>
            Add another machine
          </Button>
        </div>
        <p className="text-ui text-fg-muted">A build box, a laptop or a cloud VM: run the Orgus agent there and approve it here; it needs no open ports.</p>
        <Button variant="ghost" size="sm" className="self-start text-fg-subtle" icon={dev ? <ChevronDown /> : <ChevronRight />} aria-expanded={dev} onClick={() => setDev((v) => !v)}>
          Check which addresses reach this hub
        </Button>
        {dev && <Reachability />}
      </div>

      <AddMachineDialog open={adding} onOpenChange={setAdding} onOpenMachine={(id) => navigate(`/machines?machine=${encodeURIComponent(id)}`)} />
    </div>
  );
}
